import Styles from './Template22.scss';

import Template from './Template22.hbs';
import WrongAnswerTemplate from './WrongAnswer.hbs'
import Util from 'util/util'
import TextSimilarityUtil from 'util/TextSimilarityUtil'
import Table from './table/Table'
import TableStyles from './table/Table.scss'
import TaskModel from 'models/TaskModel'
import {createTableImagesLightbox} from 'views/components/taskGroups/tasks/tableTemplate/TableUtils'

export default class Template22 extends BaseView {

    debounceIsActive = false

    initialize({
        task_view,
    }) {

        _.bindAll(this,
            'lockAnswer',
            'saveResponse',
            'getCorrectAnswer'
        );

        // Make the taskview from options accesible in the this scope
        this.taskView = task_view;

        // Get the task info json from the model
        this.taskInfoJson = this.model.get('task_info_json');

        // Set grading strictness.
        // Keep compatibility with the old 'caseSensitive' grading.
        const { gradingStrictness, caseSensitive } = this.taskInfoJson?.options ?? {}
        if (gradingStrictness !== undefined) {
            this.gradingStrictness = gradingStrictness
        } else if (caseSensitive) {
            this.gradingStrictness = TaskModel.CASE_SENSITIVE_GRADING
        } else {
            this.gradingStrictness = TaskModel.CASE_INSENSITIVE_GRADING
        }

        this.renderTask()
    }

    /**
     * This method will render the template of the task. It will be
     * overwritten with an empty method in /views/components/taskGroups/tasks/Task.js
     * if only the answer view is necessary
     */
    renderTask() {

        // Debounce the saveResponse method by 1000 ms so it only fires when more
        // than 1000 ms has elapsed since the last time it was called. This
        // is to prevent DoSS-ing ourselfs every time the user types something
        // in a cell input field.
        this.saveResponseDebounced = _.debounce(this.saveResponse, 1000)

        this.setElement(Template({Styles}))

        this.table = this.addChildView(new Table({
            tableData: this.taskInfoJson.gridCells,
            columnWidths: this.taskInfoJson.columnWidths,
            response: this.taskView.response.get('json_answer'),
        }), this.$el)

        this.table.$('td .js-input[contenteditable="true"]').on('input', this.saveResponseDebounced)

        createTableImagesLightbox(this, TableStyles)

        if (this.taskInfoJson.options?.hideBorders) {
            this.table.el.setAttribute('data-hide-borders', true)
        }

    }

    getGapCellAnswers(cell) {
        const correctCellGapAnswers = [cell.cellText || '']
        if (Array.isArray(cell.variants)) {
            for (const variant of cell.variants) {
                correctCellGapAnswers.push(variant.text)
            }
        }
        return correctCellGapAnswers.map((answer) => {
            const text = Util.stripTags(Util.normalizeCharacters(answer))
            if (this.gradingStrictness !== TaskModel.CASE_SENSITIVE_GRADING) {
                return text.toLowerCase()
            }
            return text
        })
    }

    // Check if a student answer matches any of the correct answers, using either
    // exact or fuzzy matching. Inputs should already be normalised.
    isCorrectGapAnswer(studentAnswer, correctAnswers) {
        if (this.gradingStrictness === TaskModel.TEXT_SIMILARITY_GRADING) {
            return correctAnswers.some((answer) => {
                return TextSimilarityUtil.getTextSimilarity(studentAnswer, answer)
                    >= TextSimilarityUtil.SIMILARITY_THRESHOLD
            })
        }
        return correctAnswers.includes(studentAnswer)
    }

    /**
     * This function will be called when the user or teacher click on the show answer
     * button. It will tell the student/teacher which cells where filled in wrong or
     * correct.
     */
    showAnswer() {

        // If a student clicks the show answer button while the debounce is active,
        // save the response first and then show the answer
        if (this.debounceIsActive) {
            this.saveResponseDebounced.cancel()
            this.saveResponse()
        }

        // Loop through each cell that is a gap
        for (const cell of this.table.gapCells) {

            const cellElement = this.table.getCellElement(cell.rowIndex, cell.columnIndex, true)

            let cellGapResponse = Util.stripScripts(Util.normalizeCharacters(cellElement.innerText))
            if (this.gradingStrictness !== TaskModel.CASE_SENSITIVE_GRADING) {
                cellGapResponse = cellGapResponse.toLowerCase()
            }

            const correctCellGapAnswers = this.getGapCellAnswers(cell)

            if (
                this.model.get('grading_mode') !== 'student' &&
                this.isCorrectGapAnswer(cellGapResponse, correctCellGapAnswers)
            ) {
                cellElement.classList.add(TableStyles['is-correct'])
            } else {

                if (this.model.get('grading_mode') !== 'student') {
                    cellElement.classList.add(TableStyles['is-incorrect'])
                }

                // Create a wrong answer tooltip element
                cellElement.closest('TD').insertAdjacentHTML('afterbegin', WrongAnswerTemplate({
                    Styles,
                    studentGradingMode: this.model.get('grading_mode') === 'student',
                    cell,
                }))

            }

        }
    }

    showAuthorAnswer() {
        for (const cell of this.table.gapCells) {
            const cellElement = this.table.getCellElement(cell.rowIndex, cell.columnIndex, true)
            cellElement.innerText = this.table.getGapText(cell.rowIndex, cell.columnIndex, true)
        }
    }

    /**
     * This function will be called when the user wants to see the model answer.
     * It will return a string which can be HTML and
     * that HTML will be shown for each student
     *
     * @return {string}  String containing the correct answer (HTML)
     */
    getCorrectAnswer() {

        const correctAnswerTable = new Table({
            tableData: this.taskInfoJson.gridCells,
            columnWidths: this.taskInfoJson.columnWidths,
            isReadOnly: true
        })

        for (const cell of correctAnswerTable.gapCells) {
            const cellElement = correctAnswerTable.getCellElement(cell.rowIndex, cell.columnIndex, true)
            cellElement.innerText = correctAnswerTable.getGapText(cell.rowIndex, cell.columnIndex, true)
            cellElement.classList.add(TableStyles['is-stateless'])
        }

        return correctAnswerTable

    }

    /**
     * This function will be called for each student for when the users clicks
     * on student answers button. It will return a string which can be HTML and
     * that HTML will be shown for each student
     *
     * @param  {Backbone.Model} responseModel   The response model for each student
     * @return {string}                         String containing a student answer (HTML)
     */
    getStudentAnswer(responseModel) {
        // Create a variable for response's json_answer
        const response = responseModel.get('json_answer')

        if (!response) {
            return ''
        }

        const studentResponseTable = new Table({
            tableData: this.taskInfoJson.gridCells,
            columnWidths: this.taskInfoJson.columnWidths,
            response,
            isReadOnly: true
        })

        for (const cell of studentResponseTable.gapCells) {

            const cellElement = studentResponseTable.getCellElement(cell.rowIndex, cell.columnIndex, true)

            let gapCellText = studentResponseTable.getGapText(cell.rowIndex, cell.columnIndex)

            if (this.model.get('grading_mode') === 'auto') {

                if (this.gradingStrictness !== TaskModel.CASE_SENSITIVE_GRADING) {
                    gapCellText = gapCellText.toLowerCase()
                }

                const correctCellGapAnswers = this.getGapCellAnswers(cell)

                if (this.isCorrectGapAnswer(gapCellText, correctCellGapAnswers)) {
                    cellElement.classList.add(TableStyles['is-correct'])
                } else {
                    cellElement.classList.add(TableStyles['is-incorrect'])
                }

            } else {
                cellElement.classList.add(TableStyles['is-stateless'])
            }

        }

        return studentResponseTable

    }

    /**
     * This function will be called when the user already has the answers visible
     * and click again on the answers button. This will hide the answers for the student
     * and bring the table back to its original state before the answers where made
     * visible
     */
    hideAnswer() {
        for (const cell of this.table.gapCells) {
            const cellElement = this.table.getCellElement(cell.rowIndex, cell.columnIndex, true)
            cellElement.classList.remove(TableStyles['is-correct'], TableStyles['is-incorrect'])
            cellElement.closest('TD').querySelector('.js-tooltip')?.remove()
            cellElement.innerText = Util.normalizeCharacters(cellElement.innerText)
        }
    }

    lockAnswer() {
        const needsScoring = this.taskView.response.get('need-scoring')

        for (const cell of this.table.gapCells) {
            const cellElement = this.table.getCellElement(cell.rowIndex, cell.columnIndex, true)
            cellElement.contentEditable = 'false'

            if (needsScoring) {
                cellElement.addEventListener('click', this.taskView.openScoreFirstMessage)
            }
        }
    }

    unlockAnswer() {
        for (const cell of this.table.gapCells) {
            const cellElement = this.table.getCellElement(cell.rowIndex, cell.columnIndex, true)
            cellElement.contentEditable = 'true'
            cellElement.removeEventListener('click', this.taskView.openScoreFirstMessage)
        }
    }

    /**
     * This function can be called to save the users response to the database. It will
     * loop trough all the columns that should be a gap and store the content of that cell
     * into the response object.
     */
    saveResponse() {

        this.debounceIsActive = false

        // For each gap, get it's current user input.
        const responseObject = this.table.gapCells.map((cell) => {

            // Get the gridCell's element
            const cellElement = this.table.getCellElement(cell.rowIndex, cell.columnIndex, true)

            // Add a new object to the response object
            return {

                // Save the row
                row: cell.rowIndex,

                // Save the column
                column: cell.columnIndex,

                // Save the text
                text: Util.normalizeCharacters(cellElement.innerText)
            }

        })

        // Save the updated response
        this.taskView.saveResponse(responseObject)

    }

    beforeDestroy() {

        if (!this.debounceIsActive) {
            return
        }

        this.saveResponseDebounced.cancel()

        this.saveResponse()

    }

}
